import { useEffect, useState } from "react";
import { Button, Spinner, Table } from "react-bootstrap";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { keyCrmCreatedBetweenUtcFromRange } from "../../helpers/utils";
import { TIMEZONE } from "../../constans";

dayjs.extend(utc);
dayjs.extend(timezone);

const EXCLUDED_TAG_NAME_PATTERNS = [/опт/i, /бартер/i, /без\s*чек/i];
const isExcludedTag = (tag) => EXCLUDED_TAG_NAME_PATTERNS.some((re) => re.test(tag?.name || ""));

/** Ті самі "живі" статуси, що й у решти звітів (без службових/сміттєвих, без повернень/скасувань). */
const RELEVANT_STATUS_IDS = [1, 6, 8, 9, 10, 11, 22, 26, 12, 30, 31, 32, 33, 34];

const crmOrderUrl = (id) => `https://lavastudio.keycrm.app/app/orders/view/${id}`;

export const TodayUnfiscalizedOrders = () => {
    const [status, setStatus] = useState("loading");
    const [orders, setOrders] = useState([]);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const today = new Date();
        const { startUtc, endUtc } = keyCrmCreatedBetweenUtcFromRange(today, today);
        const params = [
            "limit=500",
            "sort=id",
            encodeURIComponent("filter[created_between]") + "=" + encodeURIComponent(`${startUtc}, ${endUtc}`),
            encodeURIComponent("filter[status_id]") + "=" + encodeURIComponent(RELEVANT_STATUS_IDS.join(",")),
            "include=" + encodeURIComponent("buyer, manager, status, tags"),
        ].join("&");

        (async () => {
            setStatus("loading");
            try {
                const res = await fetch(`/api/orders?${params}`, { credentials: "include" });
                if (!res.ok) throw new Error(res.statusText);
                const data = await res.json();
                if (cancelled) return;

                /** "Без фіскалізації" = fiscal_status зовсім порожній (null). "partial"/"done" — вже є чек, не показуємо. */
                const list = (Array.isArray(data) ? data : [])
                    .filter((o) => o && Object.keys(o).length > 0)
                    .filter((o) => !(o.tags || []).some(isExcludedTag))
                    .filter((o) => !o.fiscal_status);

                setOrders(list);
                setStatus("ok");
            } catch {
                if (cancelled) return;
                setStatus("error");
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    if (status === "error") return null;

    const count = orders.length;

    return (
        <div className="orders-page-unfiscalized px-3 pt-2 pb-1">
            <Button
                variant="outline-dark"
                size="sm"
                onClick={() => setIsOpen((v) => !v)}
                disabled={status === "loading"}
            >
                {status === "loading" ? (
                    <Spinner animation="border" size="sm" />
                ) : (
                    `Перевірка фіск (${count})`
                )}
            </Button>
            {isOpen && status === "ok" && (
                count === 0 ? (
                    <div className="text-muted small mt-2">Усі замовлення сьогодні фіскалізовані.</div>
                ) : (
                    <div className="orders-page-unfiscalized-table-holder mt-2">
                        <Table striped bordered hover responsive size="sm" className="orders-page-unfiscalized-table mb-0">
                            <thead>
                            <tr>
                                <th>№</th>
                                <th>Час створення</th>
                                <th>Покупець</th>
                                <th>Сума</th>
                            </tr>
                            </thead>
                            <tbody>
                            {orders.map((o) => (
                                <tr key={o.id}>
                                    <td>
                                        <a href={crmOrderUrl(o.id)} target="_blank" rel="noopener noreferrer" style={{ color: "#0d6efd" }}>
                                            {o.id}
                                        </a>
                                    </td>
                                    <td>{dayjs(o.created_at).tz(TIMEZONE).format("HH:mm")}</td>
                                    <td>{o.buyer?.full_name || "—"}</td>
                                    <td>{Math.floor(o.grand_total || 0).toLocaleString("uk-UA")} грн</td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </div>
                )
            )}
        </div>
    );
};

export default TodayUnfiscalizedOrders;
